import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Grid, 
  Button, 
  Chip,
  AppBar,
  Toolbar
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/gemistruct-logo.png';
import wordmark from '../assets/gemistruct-wordmark.png';

export default function TermsOfUse() {
  const navigate = useNavigate();

  return (
    <>
      {/* Header */}
      <AppBar 
        position="static" 
        sx={{ 
          backgroundColor: 'white',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          zIndex: 10,
        }} 
        elevation={0}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            {/* Clickable Logo that links to Home */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 }
              }}
              onClick={() => navigate('/')}
            >
              <img src={logo} alt="Gemistruct Logo" style={{ height: 48, marginRight: 16 }} />
              <img src={wordmark} alt="Gemistruct" style={{ height: 36 }} />
            </Box>
          </Box>
          
          {/* Navigation Links */}
          <Box sx={{ display: 'flex', gap: 2, mr: 3 }}>
            <Button 
              sx={{ 
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                }
              }}
              onClick={() => navigate('/consulting')}
            >
              CONSULTING SERVICES
            </Button>
            <Button 
              sx={{ 
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'secondary.main',
                  color: 'white',
                }
              }}
              onClick={() => navigate('/engineering-tools')}
            >
              ENGINEERING TOOLS
            </Button>
            <Button 
              sx={{ 
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                }
              }}
              onClick={() => {
                navigate('/#about');
                setTimeout(() => {
                  document.getElementById('about')?.scrollIntoView({ 
                    behavior: 'smooth',
                    block: 'start'
                  });
                }, 100);
              }}
            >
              ABOUT US
            </Button>
            <Button 
              sx={{ 
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                }
              }}
              onClick={() => {
                navigate('/#contact');
                setTimeout(() => {
                  document.getElementById('contact')?.scrollIntoView({ 
                    behavior: 'smooth',
                    block: 'start'
                  });
                }, 100);
              }}
            >
              CONTACT
            </Button>
          </Box>
          
          <Button 
            color="primary" 
            variant="contained" 
            sx={{ 
              fontWeight: 600,
              '&:hover': {
                transform: 'scale(1.05)',
                transition: 'transform 0.2s ease-in-out',
              }
            }}
            onClick={() => navigate('/signin')}
          >
            Sign In
          </Button>
        </Toolbar>
      </AppBar>

      {/* Content */}
      <Container maxWidth="md">
        <Box sx={{ py: 8 }}>
          <Paper elevation={0} sx={{ p: 4 }}>
            <Typography variant="h3" gutterBottom fontWeight={700}>
              Gemistruct Terms of Use (EULA)
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Effective Date: {new Date().toLocaleDateString()}
            </Typography>
            
            <Typography paragraph sx={{ mt: 4 }}>
              This End User License Agreement ("Agreement") is between you ("the user") and Gemistruct Pty Ltd (ACN 684 710 594), a company registered in Queensland, Australia. Use of the Gemistruct platform is governed by the terms set out below, in accordance with the laws of Queensland, Australia.
            </Typography>
            
            <Typography paragraph>
              By accessing or using Gemistruct, you confirm that you have read, understood, and agree to these terms. If you do not accept these terms, you must not use the software.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 600 }}>
              1. License & Permitted Use
            </Typography>
            <Typography paragraph>
              • Gemistruct is provided free of charge for testing, research, and evaluation purposes only.
            </Typography>
            <Typography paragraph>
              • The software is currently in a beta phase and is not suitable for real-world design, construction, or any legally binding engineering decisions.
            </Typography>
            <Typography paragraph>
              • No warranties, representations, or guarantees are provided as to the accuracy, completeness, or fitness for purpose of the outputs.
            </Typography>
            <Typography paragraph>
              • Users are solely responsible for verifying any results and must apply independent engineering judgment.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 600 }}>
              2. Intellectual Property
            </Typography>
            <Typography paragraph>
              • All content, design tools, code, graphics, and materials within Gemistruct are the intellectual property of Gemistruct Pty Ltd.
            </Typography>
            <Typography paragraph>
              • You are granted a limited, non-exclusive, non-transferable license to use the software in accordance with these terms.
            </Typography>
            <Typography paragraph>
              • You must not reproduce, reverse-engineer, distribute, or resell any part of the software without prior written consent.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 600 }}>
              3. Engineering Liability Disclaimer
            </Typography>
            <Typography paragraph>
              • All calculations and results generated are theoretical and provided for demonstration purposes only.
            </Typography>
            <Typography paragraph>
              • Gemistruct Pty Ltd accepts no liability for any direct, indirect, incidental, or consequential loss, damage, or claims arising from reliance on the software's outputs.
            </Typography>
            <Typography paragraph>
              • Results must be reviewed and verified by a qualified structural engineer before being applied in any real-world engineering or construction context.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 600 }}>
              4. User Accounts
            </Typography>
            <Typography paragraph>
              • User accounts are intended for individual use only and may not be shared, sold, or transferred.
            </Typography>
            <Typography paragraph>
              • You are responsible for maintaining the confidentiality of your account credentials.
            </Typography>
            <Typography paragraph>
              • Any misuse or unauthorised access may result in account suspension or termination.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 600 }}>
              5. Service Availability
            </Typography>
            <Typography paragraph>
              • Gemistruct may be updated, modified, suspended, or discontinued at any time without prior notice.
            </Typography>
            <Typography paragraph>
              • The platform is provided on an "as-is" and "as-available" basis, with no guarantee of uptime, ongoing support, or data retention.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 600 }}>
              6. Privacy & Data
            </Typography>
            <Typography paragraph>
              • Personal information (such as your name, email address, or usage data) may be collected and handled in accordance with the Australian Privacy Act 1988 (Cth) and our{' '}
              <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                <Button 
                  color="inherit" 
                  sx={{ 
                    textTransform: 'none', 
                    fontSize: 'inherit',
                    color: 'primary.main',
                    fontWeight: 600,
                    p: 0,
                    minWidth: 'auto',
                    textDecoration: 'underline'
                  }}
                  onClick={() => navigate('/privacy')}
                >
                  Privacy Policy
                </Button>
              </Box>
              .
            </Typography>
            <Typography paragraph>
              • Data is used internally to operate, maintain, and improve the user experience and tools.
            </Typography>
            <Typography paragraph>
              • We do not sell or rent personal information to third parties.
            </Typography>
            <Typography paragraph>
              • Users may request deletion of their personal data at any time by contacting{' '}
              <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                <a href="mailto:info@gemistruct.com" style={{ textDecoration: 'none', color: 'inherit' }}>
                  info@gemistruct.com
                </a>
              </Box>
              .
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 600 }}>
              7. Governing Law
            </Typography>
            <Typography paragraph>
              This Agreement is governed by the laws of Queensland, Australia. Any disputes arising from the use of the software will be subject to the exclusive jurisdiction of the courts in Queensland.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 600 }}>
              8. Contact Us
            </Typography>
            <Typography paragraph>
              If you have questions or concerns about these Terms of Use, please contact:
            </Typography>
            <Typography paragraph sx={{ fontWeight: 600 }}>
              Gemistruct Pty Ltd
            </Typography>
            <Typography paragraph>
              Email: <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                <a href="mailto:info@gemistruct.com" style={{ textDecoration: 'none', color: 'inherit' }}>
                  info@gemistruct.com
                </a>
              </Box>
            </Typography>

            <Box sx={{ mt: 6, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography color="text.secondary">
                This agreement is effective as of the date listed above and may be updated from time to time.
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: 'grey.900', color: 'grey.100', py: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                {/* Company name in text instead of logo */}
                <Typography variant="h5" fontWeight={700} sx={{ mb: 1, color: 'white' }}>
                  Gemistruct
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: 'grey.300' }}>
                  Professional consulting and cloud-based engineering tools for Australian structural engineering industry.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                   <Chip label="CPEng" size="small" sx={{ 
                     color: 'white', 
                     borderColor: 'white', 
                     '& .MuiChip-label': { color: 'white' }
                   }} variant="outlined" />
                   <Chip label="RPEQ" size="small" sx={{ 
                     color: 'white', 
                     borderColor: 'white', 
                     '& .MuiChip-label': { color: 'white' }
                   }} variant="outlined" />
                   <Chip label="NER" size="small" sx={{ 
                     color: 'white', 
                     borderColor: 'white', 
                     '& .MuiChip-label': { color: 'white' }
                   }} variant="outlined" />
                 </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Quick Links</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button 
                  color="inherit" 
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  onClick={() => navigate('/')}
                >
                  Home
                </Button>
                <Button 
                  color="inherit" 
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  onClick={() => navigate('/consulting')}
                >
                  Consulting Services
                </Button>
                <Button 
                  color="inherit" 
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  onClick={() => navigate('/engineering-tools')}
                >
                  Engineering Tools
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ borderTop: 1, borderColor: 'grey.800', pt: 3, mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'grey.100' }}>
              Copyright © {new Date().getFullYear()} GEMISTRUCT PTY LTD ABN: 80 684 710 594{' '}
              <Button 
                color="inherit" 
                sx={{ 
                  textTransform: 'none', 
                  fontSize: '0.875rem',
                  color: 'grey.100',
                  '&:hover': { color: 'white' },
                  p: 0,
                  minWidth: 'auto'
                }}
                onClick={() => navigate('/privacy')}
              >
                Privacy
              </Button>
              {' '}|{' '}
              <Button 
                color="inherit" 
                sx={{ 
                  textTransform: 'none', 
                  fontSize: '0.875rem',
                  color: 'grey.100',
                  '&:hover': { color: 'white' },
                  p: 0,
                  minWidth: 'auto'
                }}
                onClick={() => navigate('/terms')}
              >
                Terms & Conditions
              </Button>
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
} 